import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { UserRole } from '../types';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Users,
  Building2,
  MapPin,
  Megaphone,
  Briefcase,
  Calendar,
  Tags,
  Box,
  Star,
  FileCheck,
  LogOut,
  User,
  Globe,
  Map,
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface MenuItemType {
  text: string;
  icon: React.ReactElement;
  path: string;
  roles?: UserRole[];
}

const menuItems: MenuItemType[] = [
  {
    text: 'Dashboard',
    icon: <LayoutDashboard className="h-5 w-5" />,
    path: '/',
  },
  {
    text: 'User Management',
    icon: <Users className="h-5 w-5" />,
    path: '/users',
  },
  {
    text: 'Organizations',
    icon: <Building2 className="h-5 w-5" />,
    path: '/organizations',
  },
  {
    text: 'Branches',
    icon: <MapPin className="h-5 w-5" />,
    path: '/branches',
  },
  {
    text: 'Branch Requests',
    icon: <FileCheck className="h-5 w-5" />,
    path: '/branch-requests',
  },
  {
    text: 'Campaigns',
    icon: <Megaphone className="h-5 w-5" />,
    path: '/campaigns',
  },
  {
    text: 'Job Listings',
    icon: <Briefcase className="h-5 w-5" />,
    path: '/jobs',
  },
  {
    text: 'Operations',
    icon: <Calendar className="h-5 w-5" />,
    path: '/operations',
  },
  {
    text: 'Units',
    icon: <Box className="h-5 w-5" />,
    path: '/units',
  },
  {
    text: 'Reviews',
    icon: <Star className="h-5 w-5" />,
    path: '/reviews',
  },
  {
    text: 'Business Categories',
    icon: <Tags className="h-5 w-5" />,
    path: '/business-categories',
    roles: [UserRole.SYS_ADMIN],
  },
  {
    text: 'Countries',
    icon: <Globe className="h-5 w-5" />,
    path: '/countries',
    roles: [UserRole.SYS_ADMIN],
  },
  {
    text: 'Cities',
    icon: <Map className="h-5 w-5" />,
    path: '/cities',
    roles: [UserRole.SYS_ADMIN],
  },
];

export const AppSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const filteredMenuItems = menuItems.filter((item) => {
    if (!item.roles) return true;
    return user && item.roles.includes(user.role);
  });

  return (
    <div className="flex h-screen w-64 flex-col border-r bg-card">
      {/* Logo */}
      <div className="flex h-16 items-center border-b px-6">
        <h1 className="text-xl font-bold text-primary">Turnect Internal</h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="space-y-1">
          {filteredMenuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Button
                key={item.path}
                variant={isActive ? 'secondary' : 'ghost'}
                className={cn('w-full justify-start gap-3', isActive && 'bg-secondary font-medium')}
                onClick={() => navigate(item.path)}
              >
                {item.icon}
                <span>{item.text}</span>
              </Button>
            );
          })}
        </div>
      </nav>

      {/* User Profile */}
      <div className="border-t p-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start gap-3 px-2">
              <Avatar className="h-8 w-8">
                <div className="flex h-full w-full items-center justify-center bg-primary text-primary-foreground">
                  <User className="h-4 w-4" />
                </div>
              </Avatar>
              <div className="flex flex-col items-start text-sm">
                <span className="font-medium">{user?.fullName}</span>
                <span className="text-xs text-muted-foreground">{user?.role}</span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
